import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import get from 'lodash/get';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Nav,
  NavItem,
  NavLink,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';

import {
  fetchDocuments,
  downloadDocument,
  selectLoanId,
} from 'actions/dashboard';

import { IconDocument } from 'components/Icons';
import Loader from 'components/Loader';
import { ButtonLink } from 'components/Button';
import './styles.scss';

const DOCUMENT_TABS = [
  { id: 'notices', label: 'Notices' },
  { id: 'statements', label: 'Statements' },
  { id: 'agreements', label: 'Agreements' },
  { id: 'receipts', label: 'Receipts' },
];

const getDocumentSection = (document) => String(document.section || '').toLowerCase();

const normalizeDocumentsList = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.documents)) {
    return data.documents;
  }

  return [];
};

const getDocumentDisplayName = (document) => (
  get(document, 'fileAttachment.fileOriginalName')
  || get(document, 'fileOriginalName')
  || get(document, 'fileName')
  || get(document, 'name')
  || '-'
);

const getDocumentFileName = (document) => (
  get(document, 'fileAttachment.fileName')
  || get(document, 'fileName')
  || get(document, 'fileOriginalName')
  || get(document, 'name')
  || ''
);

const getDocumentCreatedDate = (document) => get(document, 'timestamp');

const getDocumentId = (document) => get(document, 'id') || get(document, 'documentId');

const ConsumerDocuments = (props) => {
  const { loansList, documents } = props;
  const [activeTab, setActiveTab] = useState('notices');
  const documentsData = get(documents, 'data');
  const documentslist = useMemo(() => normalizeDocumentsList(documentsData), [documentsData]);

  useEffect(() => {
    props.fetchDocuments(loansList.activeLoanId);
  }, []);

  const filteredDocuments = useMemo(() => (
    documentslist.filter((document) => getDocumentSection(document) === activeTab)
  ), [documentslist, activeTab]);

  const handleChange = (e) => {
    const nextLoanId = e.target.value;
    if (nextLoanId != null && nextLoanId !== '') {
      localStorage.setItem('selectedLoanId', String(nextLoanId));
    }
    props.selectLoanId(nextLoanId);
    props.fetchDocuments(nextLoanId);
  };

  const download = (document) => {
    const fileId = getDocumentId(document);
    const fileName = getDocumentFileName(document);

    if (!fileId || !fileName) {
      return;
    }

    props.downloadDocument(`${loansList.activeLoanId}&${fileId}&${fileName}`);
  };

  const handleTabChange = (newActiveTab, e) => {
    e.preventDefault();
    setActiveTab(newActiveTab);
  };

  return (
    <div className="page-documents pb-3">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col className="text-center text-md-left">
            <h3 className="mb-0">Documents</h3>
          </Col>
          <Col xs={12} md="auto" className="text-right justify-content-end ml-md-3 mt-3 mt-md-0">
            <div className="mb-0 input-inline pb-0 form-group">
              <UncontrolledDropdown inNavbar>
                <DropdownToggle
                  nav
                  caret
                  style={{
                    padding: '.675rem 1.125rem',
                    height: 'auto',
                    backgroundColor: 'white',
                    border: '2px solid #dbe3eb',
                    color: '#495057',
                    fontSize: '1rem',
                    marginBottom: '0px !important',
                  }}
                  className="custom-dropdown-toggle form-control"
                >
                  {loansList.activeLoanId && loansList.data
                    ? loansList.data.find((item) => String(item.loanId) === String(loansList.activeLoanId))?.externalId || 'Select'
                    : 'Select'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem end>Select</DropdownItem>
                  {loansList.data && loansList.data.map((item, index) => (
                    <DropdownItem
                      key={`loanlist_${index}`}
                      onClick={() => handleChange({ target: { value: item.loanId } })}
                      active={String(loansList.activeLoanId) === String(item.loanId)}
                    >
                      {item.externalId}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={0} lg={12}>
            <Card>
              <Nav tabs>
                {DOCUMENT_TABS.map(({ id, label }) => (
                  <NavItem key={id}>
                    <NavLink
                      className={activeTab === id ? 'active' : ''}
                      onClick={handleTabChange.bind(null, id)}
                    >
                      {label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
              {documents.isLoading || documentsData === undefined ? (
                <Loader className="height-2" />
              ) : (
                <CardBody style={{ maxHeight: 595, overflowY: 'auto' }}>
                  <Table className="mb-0" size="sm" striped borderless>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Date</th>
                        <th>Download</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDocuments.length > 0 ? filteredDocuments.map((item, index) => {
                        const createdDate = getDocumentCreatedDate(item);

                        return (
                          <tr key={getDocumentId(item) || index}>
                            <td>
                              <IconDocument className="mr-1" />
                              {' '}
                              {getDocumentDisplayName(item)}
                            </td>
                            <td>
                              {createdDate
                                ? moment(new Date(createdDate)).format('MM/DD/YYYY')
                                : '-'}
                            </td>
                            <td>
                              {getDocumentFileName(item) ? (
                                <ButtonLink onClick={download.bind(null, item)} className="btn-icon" style={{ cursor: 'pointer' }}>
                                  <img src={`${process.env.PUBLIC_URL}/icons/download.svg`} alt="download" style={{ width: 20, height: 20 }} />
                                </ButtonLink>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={4} align="center" className="pt-3 pb-3">No data</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>

      </Container>

    </div>
  );
};

ConsumerDocuments.propTypes = {
  selectLoanId: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func.isRequired,
  downloadDocument: PropTypes.func.isRequired,
  loansList: PropTypes.object.isRequired,
  documents: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  loansList: state.dashboard.loansList,
  documents: state.dashboard.documents,
});

const mapDispatchToProps = {
  fetchDocuments,
  downloadDocument,
  selectLoanId,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(ConsumerDocuments);
